import {
    Contract,
    ContractSignatureStatus,
    ContractState,
    EarningType,
    Mission,
    PayrollSourceType,
    ProfileType,
    Repetition,
} from "../../__generated__/graphql";
import React, { useCallback, useContext, useMemo } from "react";
import { Linking, StyleProp, StyleSheet, View, ViewStyle } from "react-native";
import { useTheme } from "../../hooks/use-theme";
import { useFormatContractWorkingTime, useI18n } from "../../hooks/i18n-hooks";
import {
    BalanceType,
    formatActivityTypeTitle,
    formatMissionClientName,
    formatMissionClientProfileName,
    formatMissionWorkerName,
    openPhoneNumber,
} from "../../utils";
import {
    useBuildMissionDeductionDescription,
    useBuildMissionFeeDescription,
    useMissionDisplayAttributes,
} from "../../hooks/missions";
import SalaryProjections from "../SalaryProjections";
import { isAfter } from "date-fns";
import ListItem from "../../ui_atoms/ListItem";
import ListHeader from "../../ui_atoms/ListHeader";
import { StackNavigationProp } from "@react-navigation/stack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import Label from "../../ui_atoms/Label";
import Gap from "../../ui_atoms/Gap";
import DocumentsList from "../document/DocumentsList";
import { useContractCct } from "../../hooks/cct-utils";
import { useIntercom } from "../../hooks/intercom";
import VStack from "../../ui_atoms/VStack";
import { Alert } from "../Alert";
import { useMutation, useQuery } from "@apollo/client";
import { CHANGE_MISSION_OWNER, GET_MISSION, UPDATE_MISSION } from "../../graphql/core";
import { useSnackbar } from "../../hooks/snackbar";
import { IconName } from "../Icon";
import { useBuildIjmDescription } from "../../hooks/ijm";
import { AuthManagerContext } from "../../contexts/AuthManager";
import { SelectChooser, SelectItemType } from "../select/SelectChooser";
import MarkdownView from "../MarkdownView";
import { GET_CLIENT_USERS } from "../../graphql/client";
import { useCurrentProfile } from "../../hooks/use-current-profile";
import MissionDetailsStatus from "./MissionDetailsStatus";

type BalanceInfoComponentProps = {
    i18nKey: string;
    icon: IconName;
    balanceStatus?: [boolean?, BalanceType?]; // [isFetching, balance]
};

const BalanceInfoComponent: React.FC<BalanceInfoComponentProps> = ({
    i18nKey,
    icon,
    balanceStatus = [undefined, undefined],
}) => {
    const i18n = useI18n();

    const [loading, balance] = balanceStatus;
    if (!balance) {
        return null;
    }
    const info = balance?.text;
    const infoDate = balance?.date;
    if (info == undefined || loading == undefined) {
        return null;
    }
    const tags = infoDate
        ? [i18n.t("mission.details.balance_date", { date: infoDate })]
        : undefined;
    return (
        <ListItem
            loading={loading}
            icon={icon}
            title={i18n.t(i18nKey)}
            description={info}
            tags={tags}
        />
    );
};

type MissionDetailsProps = {
    mission: Mission;
    style?: StyleProp<ViewStyle>;
    isNew?: boolean;
    navigation?: StackNavigationProp<RootStackParamList, "MissionDetails">;
    profileType: ProfileType;
    holidayBalance?: [boolean?, BalanceType?]; // [isFetching, balance]
    overtimeBalance?: [boolean?, BalanceType?]; // [isFetching, balance]
};

const MissionDetails: React.FC<MissionDetailsProps> = ({
    mission,
    style,
    navigation,
    profileType,
    isNew = false,
    holidayBalance = undefined,
    overtimeBalance = undefined,
}) => {
    const { marginSize } = useTheme();
    const [updating, setUpdating] = React.useState(false);
    const styles = useStyles();
    const { colors } = useTheme();
    const i18n = useI18n();
    const buildIjmDescription = useBuildIjmDescription();
    const { user } = useContext(AuthManagerContext);

    const [updateMission] = useMutation(UPDATE_MISSION);
    const [changeMissionOwner] = useMutation(CHANGE_MISSION_OWNER);
    const { showSnackbar } = useSnackbar();

    const currentProfile = useCurrentProfile();

    const { data: { getClientUsers = [] } = {} } = useQuery(GET_CLIENT_USERS, {
        variables: {
            clientId: currentProfile?.client?.id || "",
        },
        skip: !currentProfile?.client?.id,
    });

    const formatContractWorkingTime = useFormatContractWorkingTime();
    const getMissionDisplayAttributes = useMissionDisplayAttributes();
    const displayAttributes = useMemo(
        () => getMissionDisplayAttributes(mission, profileType),
        [getMissionDisplayAttributes, mission, profileType],
    );
    const contract = useMemo(() => mission.currentContract, [mission.currentContract]);
    const missionCct = useContractCct(contract as Contract);

    const isPendingSignatureByCurrentUser =
        contract?.signatureStatus === ContractSignatureStatus.NeedSelfSign;

    const contractState = contract?.state;

    const pdf_prefix =
        profileType === ProfileType.Client
            ? i18n.t("contract.pdf_prefix.client")
            : i18n.t("contract.pdf_prefix.worker");
    const contract_name =
        profileType === ProfileType.Client
            ? i18n.t("mission.contract_names.client")
            : i18n.t("mission.contract_names.worker");

    const salaryDetails = useMemo<
        RootStackParamList["SalaryCountingDetails"]["salaryDetails"] | null
    >(() => {
        if (!contract) return null;

        return {
            invoiceSalary: contract.invoiceSalary || 0,
            grossSalary: contract.grossSalary || 0,
            netSalary: contract.netSalary || 0,
            baseSalary: contract.baseSalary || 0,
            clientDeductions: contract.clientDeductions || [],
            workerDeductions: contract.workerDeductions || [],
            lseMargin: contract.lseMargin || 0,
            vacationCompensation: contract.vacationCompensation || 0,
            publicHolidaysCompensation: contract.publicHolidaysCompensation || 0,
            thirteenMonthCompensation: contract.thirteenMonthCompensation || 0,
        };
    }, [contract]);

    const contractEarningType = useMemo(
        () => mission.currentContract?.earningType || EarningType.Monthly,
        [mission.currentContract?.earningType],
    );

    const payrollBreakdownComponent = useMemo(
        () => (
            <ListItem
                labels={[{ text: i18n.t("mission.details.show_payroll_breakdown"), mode: "cta" }]}
                onPress={() => {
                    navigation?.navigate("PayrollBreakdownList", {
                        missionId: mission.id,
                    });
                }}
            />
        ),
        [i18n, mission.id, navigation],
    );

    const missionTimesheetsComponent = useMemo(
        () => (
            <ListItem
                labels={[{ text: i18n.t("timesheet_list.title"), mode: "cta" }]}
                onPress={() => {
                    navigation?.navigate("MissionTimeSheets", {
                        missionId: mission.id,
                    });
                }}
            />
        ),
        [i18n, mission.id, navigation],
    );

    const isContractSignatureSuccess = useMemo(
        () => mission?.currentContract?.signatureStatus === ContractSignatureStatus.Success,
        [mission?.currentContract?.signatureStatus],
    );

    const editMissionName = useCallback(() => {
        Alert.prompt(
            i18n.t("mission.edit_name"),
            i18n.t("mission.edit_name_description"),
            (text) => {
                setUpdating(true);
                void updateMission({
                    variables: {
                        id: mission.id,
                        missionData: {
                            name: text,
                        },
                        generateContractAsset: false,
                    },
                    refetchQueries: [GET_MISSION],
                })
                    .catch(() => {
                        showSnackbar({
                            text: i18n.t("mission.update_error"),
                            mode: "error",
                        });
                    })
                    .finally(() => {
                        setUpdating(false);
                    });
            },
            "plain-text",
            mission.name || "",
            "default",
        );
    }, [i18n, mission.id, mission.name, showSnackbar, updateMission]);

    const isMissionNameEditable = useMemo(
        () =>
            profileType === ProfileType.Client &&
            !updating &&
            contractState &&
            ![ContractState.Terminated, ContractState.Cancelled, ContractState.Ended].includes(
                contractState,
            ),
        [contractState, profileType, updating],
    );

    const formattedActivityTypeTitle = useMemo(
        () =>
            mission.activityType?.title &&
            formatActivityTypeTitle(mission.activityType.title, user?.gender),
        [mission.activityType?.title, user?.gender],
    );

    const { presentArticle } = useIntercom();

    const [isResponsableChooserOpen, setIsResponsableChooserOpen] = React.useState(false);

    const ownersList = useMemo<SelectItemType<string>[]>(
        () =>
            getClientUsers.map((clientUser) => ({
                label: `${clientUser.firstName} ${clientUser.lastName}`,
                value: clientUser.id,
                useBigAvatarOrIcon: true,
                avatarPath: clientUser.avatarUrl,
            })),
        [getClientUsers],
    );

    const currentMissionOwner = useMemo(
        () => getClientUsers.find((clientUser) => clientUser.id === mission.clientInfo.id),
        [getClientUsers, mission.clientInfo.id],
    );

    const buildMissionDeductionDescription = useBuildMissionDeductionDescription();
    const buildMissionFeeDescription = useBuildMissionFeeDescription();

    return (
        <View style={[styles.component, style]}>
            <VStack h="2/3" gap={1} style={{ marginVertical: marginSize }}>
                <MissionDetailsStatus mission={mission} user={user} profileType={profileType} />
            </VStack>
            {isContractSignatureSuccess &&
                (profileType === ProfileType.Client
                    ? payrollBreakdownComponent
                    : mission.payrollSource === PayrollSourceType.Lift &&
                      missionTimesheetsComponent)}

            {/* Information */}
            <ListHeader value={i18n.t("mission.details.mission_details")} />
            <ListItem
                icon="tag"
                title={i18n.t("mission.name")}
                description={mission.name || ""}
                {...(isMissionNameEditable
                    ? {
                          rightIcon: "edit",
                          rightActionMode: "action",
                          onPress: editMissionName,
                      }
                    : {})}
                loading={updating}
            />
            <ListItem
                icon="legal_employer"
                title={i18n.t("mission.legal_employer")}
                description={mission.legalEmployer?.marketingName || ""}
                labels={[{ text: i18n.t("know_more"), mode: "miniCta" }]}
                onPress={() => {
                    void presentArticle("LegalEmployerInfo");
                }}
            />
            <ListItem
                icon="duration"
                title={i18n.t("mission.period")}
                description={displayAttributes.durationInfo}
            />
            <ListItem
                icon="clock"
                title={i18n.t("mission.working_time")}
                description={formatContractWorkingTime(contract)}
            />
            <ListItem
                icon="work"
                title={i18n.t("mission.activity_name")}
                description={[
                    formattedActivityTypeTitle,
                    ...(contract?.jobTitle &&
                    !mission.activityType?.title.includes(contract.jobTitle)
                        ? [mission.currentContract?.jobTitle]
                        : []),
                ].join("\n")}
            />
            {contract?.jobDescription && (
                <ListItem
                    icon="onboarding"
                    title={i18n.t("mission.description")}
                    description={contract?.jobDescription}
                />
            )}
            {profileType === ProfileType.Client ? (
                <ListItem
                    icon="dollar-square"
                    title={i18n.t("mission.invoiced")}
                    description={displayAttributes.salaryInvoice}
                    onPress={
                        salaryDetails
                            ? () => {
                                  navigation?.navigate("SalaryCountingDetails", {
                                      earningType: contractEarningType,
                                      salaryDetails,
                                  });
                              }
                            : undefined
                    }
                />
            ) : (
                <ListItem
                    icon="dollar-square"
                    title={i18n.t("mission.gross_salary")}
                    description={displayAttributes.salaryGross}
                    onPress={
                        salaryDetails
                            ? () => {
                                  navigation?.navigate("SalaryCountingDetails", {
                                      earningType: contractEarningType,
                                      salaryDetails,
                                  });
                              }
                            : undefined
                    }
                />
            )}
            {/* <ListItem
                icon="cash"
                title={i18n.t("mission.gross_salary")}
                description={displayAttributes.salaryGross}
                onPress={
                    salaryDetails
                        ? () => {
                              navigation?.navigate("SalaryCountingDetails", {
                                  earningType: contractEarningType,
                                  salaryDetails,
                              });
                          }
                        : undefined
                }
            /> */}
            {contract?.ijmInfo && (
                <ListItem
                    icon="ijm"
                    title={i18n.t("ijm.mission.title")}
                    description={buildIjmDescription(
                        contract.ijmInfo.ijmGroup.name,
                        contract.ijmInfo.payFirstDays,
                        contract.ijmInfo.payRemainingPercents,
                    )}
                    labels={[{ text: i18n.t("know_more"), mode: "miniCta" }]}
                    onPress={() => {
                        void presentArticle(
                            profileType === ProfileType.Worker
                                ? "IJMGroupWorker"
                                : "IJMGroupClient",
                        );
                    }}
                />
            )}
            {mission.payrollSource && (
                <ListItem
                    icon="calendar-clock"
                    title={i18n.t("mission.payrollSource")}
                    description={i18n.t(`payrollSource.type.${mission.payrollSource}`)}
                />
            )}

            {displayAttributes.fees.length > 0 && (
                <ListItem
                    icon="receipt"
                    title={i18n.t("mission.details.client_fees")}
                    description={buildMissionFeeDescription({
                        fees: contract?.fees,
                        isPackage: contract?.workingTimeClassicSpec?.repetition === Repetition.None,
                        isClient: profileType === ProfileType.Client,
                    })}
                    onPress={() => {
                        navigation?.navigate("MissionClientFees", {
                            fees: displayAttributes.fees,
                        });
                    }}
                />
            )}

            {displayAttributes.deductions.length > 0 && (
                <ListItem
                    icon="receipt"
                    title={i18n.t("mission.details.mission_deductions")}
                    description={buildMissionDeductionDescription({
                        deductions: contract?.deductions,
                        isPackage: contract?.workingTimeClassicSpec?.repetition === Repetition.None,
                    })}
                    onPress={() => {
                        navigation?.navigate("MissionInfoDeductions", {
                            deductions: displayAttributes.deductions,
                        });
                    }}
                />
            )}
            {holidayBalance && (
                <BalanceInfoComponent
                    i18nKey="mission.details.holiday_balance"
                    icon="vacation"
                    balanceStatus={holidayBalance}
                />
            )}
            {overtimeBalance && (
                <BalanceInfoComponent
                    i18nKey="mission.details.overtime_balance"
                    icon="clock"
                    balanceStatus={overtimeBalance}
                />
            )}
            {/* owner */}
            {profileType === ProfileType.Client && !mission.clientInfo.isPrivateClient && (
                <>
                    <ListHeader value={i18n.t("mission.details.owner")} />
                    <ListItem
                        avatarPath={currentMissionOwner?.avatarUrl}
                        description={`${currentMissionOwner?.firstName} ${currentMissionOwner?.lastName}`}
                        rightIcon="right-left"
                        rightIconColor={colors.secondary}
                    />
                    {(getClientUsers?.length || 0) > 1 ? (
                        <ListItem
                            labels={[{ text: i18n.t("mission.details.change_owner"), mode: "cta" }]}
                            rightIcon="right-left"
                            rightIconColor={colors.secondary}
                            rightActionMode="medium"
                            onPress={() => {
                                setIsResponsableChooserOpen(true);
                            }}
                        />
                    ) : null}
                    <SelectChooser<string>
                        topComponent={
                            <VStack v="2/3" h="2/3">
                                <MarkdownView
                                    markdown={i18n.t("mission.details.name_owner_description")}
                                />
                            </VStack>
                        }
                        visible={isResponsableChooserOpen}
                        onDismiss={() => {
                            setIsResponsableChooserOpen(false);
                        }}
                        searchItems={ownersList}
                        onSelect={async (newClientUser) => {
                            setIsResponsableChooserOpen(false);
                            await changeMissionOwner({
                                variables: {
                                    missionId: mission.id,
                                    clientUserId: newClientUser.value,
                                },
                            });
                            showSnackbar({
                                mode: "success",
                                text: i18n.t("mission.details.new_owner_selected"),
                            });
                        }}
                        selectedValue={currentMissionOwner?.id}
                        halfScreen
                    />
                </>
            )}
            {/* Worker */}
            {profileType === ProfileType.Client && (
                <>
                    <ListHeader value={i18n.t("mission.worker")} />
                    <ListItem
                        avatarPath={mission.workerInfo.avatarUrl}
                        description={formatMissionWorkerName(mission)}
                    />
                    {mission.workerInfo.phoneNumber && (
                        <ListItem
                            icon="phone-call"
                            description={mission.workerInfo.phoneNumber}
                            onPress={async () => {
                                mission.workerInfo.phoneNumber &&
                                    (await openPhoneNumber(mission.workerInfo.phoneNumber));
                            }}
                            rightIcon="phone-call"
                            rightActionMode="action"
                        />
                    )}
                    {mission.workerInfo.email && (
                        <ListItem
                            icon="email"
                            description={mission.workerInfo.email}
                            onPress={async () => {
                                if (mission.workerInfo.email) {
                                    const url = `mailto:${mission.workerInfo.email}`;
                                    if (await Linking.canOpenURL(url)) {
                                        await Linking.openURL(url);
                                    }
                                }
                            }}
                            rightIcon="external-link"
                            rightActionMode="action"
                        />
                    )}
                </>
            )}
            {/* Client */}
            {profileType === ProfileType.Worker && (
                <>
                    <ListHeader value={i18n.t("mission.client")} />
                    <ListItem
                        avatarPath={mission.clientInfo.logoUrl}
                        description={formatMissionClientName(mission)}
                    />
                    {!mission.clientInfo.isPrivateClient && (
                        <ListItem
                            avatarPath={mission.clientInfo.avatarUrl}
                            description={formatMissionClientProfileName(mission)}
                        />
                    )}
                    {mission.clientInfo.phoneNumber && (
                        <ListItem
                            icon="phone-call"
                            description={mission.clientInfo.phoneNumber}
                            onPress={async () => {
                                mission.clientInfo.phoneNumber &&
                                    (await openPhoneNumber(mission.clientInfo.phoneNumber));
                            }}
                            rightIcon="phone-call"
                            rightActionMode="action"
                        />
                    )}
                    {mission.clientInfo.email && (
                        <ListItem
                            icon="email"
                            description={mission.clientInfo.email}
                            onPress={async () => {
                                if (mission.clientInfo.email) {
                                    const url = `mailto:${mission.clientInfo.email}`;
                                    if (await Linking.canOpenURL(url)) {
                                        await Linking.openURL(url);
                                    }
                                }
                            }}
                            rightIcon="external-link"
                            rightActionMode="action"
                        />
                    )}
                </>
            )}
            {/* Documents */}
            <ListHeader value={i18n.t("mission.documents")} />

            {!isPendingSignatureByCurrentUser && (
                <ListItem
                    icon="file"
                    description={contract_name}
                    badge={
                        displayAttributes.contractStatus === "signed" ? (
                            <Label
                                mode={displayAttributes.labelMode}
                                text={displayAttributes.contractStatus}
                                style={{ textAlign: "center", flex: 0, width: "100%" }}
                            />
                        ) : undefined
                    }
                    disabled={!contract?.contractEffectiveAsset?.id}
                    onPress={() => {
                        const assetId = contract?.contractEffectiveAsset?.id;
                        if (assetId) {
                            navigation?.navigate("PdfViewer", {
                                title: contract_name,
                                filename: `${pdf_prefix}-${contract?.id}`,
                                assetId: assetId,
                            });
                        }
                    }}
                />
            )}
            <DocumentsList documents={mission.documents} />
            {!missionCct?.cctContractId?.startsWith("lift-no-cct") && (
                <ListItem
                    icon="cct"
                    description={i18n.t("mission.details.cct")}
                    onPress={() => {
                        if (contract?.cctDetails && missionCct) {
                            navigation?.navigate("CctDetails", {
                                cct: missionCct,
                                cctDetails: contract.cctDetails,
                            });
                        }
                    }}
                />
            )}
            {/* Projection */}
            {!(
                contract?.effectiveEndDate &&
                isAfter(new Date(), new Date(contract.effectiveEndDate))
            ) && (
                <>
                    <Gap gap={1} />
                    <ListHeader value={i18n.t("mission.salary_projections")} />
                    <Gap gap={0.5} />
                    <SalaryProjections mission={mission} />
                </>
            )}
            <Gap gap={1} />

            {/* Actions */}
            {isContractSignatureSuccess &&
                profileType === ProfileType.Worker &&
                payrollBreakdownComponent}
            <ListItem
                labels={[{ text: i18n.t("mission.details.mission_history"), mode: "cta" }]}
                onPress={() => {
                    navigation?.navigate("MissionLogs", { missionId: mission.id });
                }}
            />
            {!isNew && displayAttributes.canCancel && (
                <ListItem
                    labels={[{ text: i18n.t("mission.cancel_mission"), mode: "redCta" }]}
                    rightActionMode="empty"
                    onPress={() => {
                        navigation?.navigate("MissionTermination", {
                            missionId: mission.id,
                        });
                    }}
                />
            )}
        </View>
    );
};

const useStyles = () => {
    const { colors, fonts } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                component: {
                    padding: 0,
                    display: "flex",
                    flex: 1,
                },
                scrollView: {
                    width: "100%",
                },
                flatView: {
                    alignSelf: "stretch",
                    flex: 0,
                    flexDirection: "column",
                    alignItems: "stretch",
                },
                documentsListGroup: {
                    flex: 1,
                    alignItems: "flex-end",
                    justifyContent: "flex-start",
                },
                errorText: {
                    color: colors.error,
                },
                reasonText: {
                    color: colors.onSurfaceDisabled,
                },
                info: {
                    ...fonts.bodyMedium,
                    color: colors.onSurfaceDisabled,
                },
            }),
        [colors.error, colors.onSurfaceDisabled, fonts.bodyMedium],
    );
};

export default MissionDetails;
